import { Button, Card, Col, Flex, Row, Tag } from '@jcm/design-system';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import CustomAlert from '../../../../../../shared/components/customAlert/customAlert';
import ModalContainer from '../../../components/modalContainer/ModalContainer';
import ObrigacaoService from '../../../services/Obrigacoes/Obrigacoes.service';
import { TipoDadoEnum } from '../../../ts/enums/TipoDadoEnum';
import { CriarAnexoPayload } from '../../../ts/types/ObrigacaoEtapa/common/Anexos';
import { TEtapa } from '../../../ts/types/ObrigacaoEtapa/Etapas/Etapa';
import { TObrigacao } from '../../../ts/types/ObrigacaoEtapa/Obrigacoes/Obrigacoes';
import { transformObrigacaoToPayload } from '../../../ts/types/serializers';
import todasEtapasConcluidas from '../../../utils/obrigacao/todasEtapasConcluidas';
import FormEtapas from '../FormEtapas';
import Anexos from '../FormObrigacoes/Anexos/Anexos';
import Acoes from './Acoes';
import Cabecalho from './Cabecalho';
import Conteudo from './Conteudo';
import style from './index.module.scss';
import Responsabilidades from './Responsabilidades';
import { EnumTipoAnexo } from '../../../ts/enums/TipoAnexoEnum';

interface CartaoObrigacaoProps {
	obrigacao: TObrigacao;
	parentCodigo?: string;
	empresaId: string | number;
	onAtualizar: () => void;
	onObrigacaoChange: (obrigacaoAtualizada: TObrigacao) => void;
}

const CartaoObrigacao: React.FC<CartaoObrigacaoProps> = ({
	obrigacao,
	parentCodigo,
	empresaId,
	onAtualizar,
	onObrigacaoChange,
}) => {
	const {
		nome,
		codigo,
		aprovadores,
		responsaveis,
		dataVencimento,
		children,
		ehLegal,
		prorrogada,
		concluida,
		anexosAcaoObrigacao,
		dataConclusao,
	} = obrigacao;

	const [mostrarEtapas, setMostrarEtapas] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [anexoData, setAnexoData] = useState<CriarAnexoPayload>({ arquivo: null, descricao: '' });
	const [modalEtapaConteudo, setModalEtapaConteudo] = useState<React.ReactNode>(null);
	const etapas = children;

	const [hasAnexos, setHasAnexos] = useState(!!anexosAcaoObrigacao?.length);
	const [alert, setAlert] = useState<{ type: 'success' | 'error'; content: string; title?: string } | null>(null);
	const [anexosAlterados, setAnexosAlterados] = useState(false);
	const [visibilidadeModalEtapa, setVisibilidadeModalEtapa] = useState<boolean>(false);

	const _obrigacoesService = new ObrigacaoService();

	const obrigacaoPayload = transformObrigacaoToPayload(obrigacao);
	const { estadoStatusAlterarData, estadoStatusCancelamento, estadoStatusAprovada } = obrigacaoPayload;
	const formattedDate = dataVencimento ? dayjs(dataVencimento).format('DD/MM/YYYY') : 'Data não disponível';
	const fullCodigo = parentCodigo ? `${parentCodigo}.${codigo}` : codigo.toString();
	const obrigacaoDesabilitada = !etapas || etapas.length === 0;
	const [etapaAtiva, setEtapaAtiva] = useState<TEtapa>();
	const [houveAlteracao, setHouveAlteracao] = useState(false);

	const handleAlteracaoDetectada = (alterado: boolean) => {
		if (alterado) {
			setHouveAlteracao(true);
		}
	};
	const [modalAnexoEtapaVisivel, setModalAnexoEtapaVisivel] = useState(false);
	const fecharAnexoEtapa = () => {
		if (anexosAlterados) {
			onAtualizar();
			setAnexosAlterados(false);
		}
		setModalAnexoEtapaVisivel(false);
	};

	useEffect(() => {
		setHasAnexos(!!anexosAcaoObrigacao?.length);
	}, [anexosAcaoObrigacao?.length]);

	useEffect(() => {
		if (alert) {
			const timer = setTimeout(() => setAlert(null), 1000);
			return () => clearTimeout(timer);
		}
	}, [alert]);

	const handleVerEtapasClick = () => setMostrarEtapas((prev: boolean) => !prev);

	const handleOpenModal = () => setIsModalVisible(true);

	const handleCloseModal = () => {
		if (anexosAlterados) {
			onAtualizar();
			setAnexosAlterados(false);
		}
		setIsModalVisible(false);
	};

	const handleAnexoChange = (field: keyof CriarAnexoPayload, value: unknown) => {
		setAnexoData((prev) => ({ ...prev, [field]: value }));
	};

	const handleAnexoSubmit = async (id: number | string) => {
		try {
			if (anexoData.arquivo) {
				const res = await _obrigacoesService.criarAnexoObrigacao(
					empresaId,
					id,
					anexoData.arquivo,
					anexoData.descricao,
					etapaAtiva?.id ? EnumTipoAnexo.EtapaObrigacao : EnumTipoAnexo.Obrigacao,
					'usuarioAtivo',
				);

				if (res.isSuccess) {
					setAnexosAlterados(true);
					setAnexoData({ arquivo: null, descricao: '' });
				}
			} else {
				setAlert({ type: 'error', content: 'Nenhum arquivo foi selecionado.' });
			}
		} catch {
			setAlert({ type: 'error', content: 'Ocorreu um erro ao criar o anexo. Tente novamente mais tarde.' });
		}
	};

	const abrirModalEtapa = () => setModalAnexoEtapaVisivel(true);
	const fecharModalEtapa = () => {
		if (houveAlteracao) {
			onAtualizar();
			setAnexosAlterados(false);
		}
		setVisibilidadeModalEtapa(false);
	};

	return (
		<Card className={style.cartaoObrigacoes} variant='default'>
			<Row gutter={[24, 24]}>
				<Cabecalho
					dataVencimento={formattedDate}
					prorrogada={prorrogada}
					ehLegal={ehLegal}
					estadoStatusAlterarData={estadoStatusAlterarData ?? 0}
					estadoStatusAprovada={estadoStatusAprovada ?? 0}
					estadoStatusCancelamento={estadoStatusCancelamento ?? 0}
					dataConclusao = {dataConclusao ?? ''}
				/>
				<Conteudo nome={nome} codigo={fullCodigo} />
				<Responsabilidades
					aprovadores={aprovadores}
					responsaveis={responsaveis}
					disabled={obrigacaoDesabilitada}
					ehLegal={ehLegal}
					onAttachClick={handleOpenModal}
					hasAnexos={hasAnexos}
				/>

				{!!etapas?.length && (
					<Col span={24}>
						<Flex>
							<Tag variant={todasEtapasConcluidas(etapas) ? 'verde' : 'amarelo'}>
								{todasEtapasConcluidas(etapas)
									? 'Todas as etapas foram concluídas.'
									: 'Esta obrigação só pode ser concluída após a finalização de todas as suas etapas.'}
							</Tag>
						</Flex>
					</Col>
				)}

				<Acoes
					onAttachClick={handleOpenModal}
					onVerEtapasClick={handleVerEtapasClick}
					disabled={obrigacaoDesabilitada}
					concluida={concluida}
					obrigacao={obrigacao}
					empresaId={empresaId}
					onObrigacaoChange={onObrigacaoChange}
					atualizarListagemDeObrigacoes={onAtualizar}
				/>
			</Row>

			<Anexos
				origem='cartao'
				data={anexoData}
				obrigacao={obrigacao}
				onChange={handleAnexoChange}
				onSave={() => {
					return handleAnexoSubmit(obrigacao.id);
				}}
				onAtualizar={onAtualizar}
				isVisible={isModalVisible}
				onClose={handleCloseModal}
				tipoDado={TipoDadoEnum.Obrigacao}
			/>

			{mostrarEtapas &&
				etapas?.map((etapa: TEtapa) => (
					<div key={etapa.codigo} className={style.subCartaoWrapper}>
						<Card className={style.subCartaoObrigacoes} variant='default'>
							<Row gutter={[24, 24]}>
								<Cabecalho
									dataVencimento={dayjs(etapa.dataVencimento).format('DD/MM/YYYY')}
									prorrogada={etapa.prorrogada}
									ehLegal={etapa.ehLegal}
									estadoStatusAlterarData={etapa?.pedidoAlteracaoDataFinal?.[0]?.estadoStatusAprovada ?? 0}
									estadoStatusAprovada={etapa?.estadoStatusAprovada ?? 0}
									estadoStatusCancelamento={etapa?.pedidoCancelarObrigacao?.[0]?.estadoStatusAprovada ?? 0}
									dataConclusao={etapa.dataConclusao ?? ''}
								/>

								<Conteudo
									nome={etapa.nome}
									codigo={`${fullCodigo}.${etapa.codigo}`}
									isSubCard
									className={style.conteudoSubCard}
								/>

								<Responsabilidades
									aprovadores={etapa.aprovadores}
									responsaveis={etapa.responsaveis}
									disabled={obrigacaoDesabilitada}
									ehLegal={etapa.ehLegal}
									onAttachClick={() => {
										abrirModalEtapa();
										setEtapaAtiva(etapa);
									}}
									hasAnexos={!!etapa.anexosAcaoObrigacao?.length}
								/>

								<Col lg={24}>
									<Button
										variant='default'
										type='outlined'
										onClick={() => {
											setModalEtapaConteudo(
												<FormEtapas
													obrigacao={obrigacao}
													etapa={etapa}
													isEdit={true}
													initialData={transformObrigacaoToPayload(etapa)}
													empresaId={empresaId}
													onClose={() => {
														fecharModalEtapa();
													}}
													onSave={() => {
														onAtualizar();
													}}
													onObrigacaoChange={onObrigacaoChange}
													onAttachClick={handleOpenModal}
													onAtualizar={onAtualizar}
													onAlteracao={handleAlteracaoDetectada}
												/>,
											);
											setVisibilidadeModalEtapa(true);
											setHouveAlteracao(false);
										}}
									>
										Editar
									</Button>
								</Col>
							</Row>
						</Card>
					</div>
				))}

			<Anexos
				origem='cartao'
				data={anexoData}
				obrigacao={obrigacao}
				etapa={etapaAtiva}
				onChange={handleAnexoChange}
				onSave={() => {
					return handleAnexoSubmit(etapaAtiva?.id || '');
				}}
				onAtualizar={onAtualizar}
				isVisible={modalAnexoEtapaVisivel}
				onClose={() => {
					setEtapaAtiva(undefined);
					fecharAnexoEtapa();
				}}
				tipoDado={TipoDadoEnum.Etapa}
			/>

			<ModalContainer
				isVisible={visibilidadeModalEtapa}
				onClose={fecharModalEtapa}
				className={`form-obrigacao ${style.modalFormContainer}`}
				key={isModalVisible ? 'visible' : 'hidden'}
			>
				{modalEtapaConteudo}
			</ModalContainer>

			{alert && <CustomAlert type={alert.type} content={alert.content} />}
		</Card>
	);
};

export default CartaoObrigacao;
